import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { useDemo } from '../context/DemoContext';
import {
  TrendingUp, TrendingDown, DollarSign, BarChart3,
  Activity, ExternalLink, RefreshCw, Zap, Droplets, ArrowUpDown,
  Users, Flame, Shield, Lock, Coins, PieChart, Eye, Trophy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCompact = (num) => {
  if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(2)}B`;
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
};
const formatPrice = (p) => p < 0.0001 ? `$${p.toFixed(8)}` : p < 1 ? `$${p.toFixed(6)}` : `$${p.toFixed(2)}`;

// ═══ PANEL ═══
const Panel = ({ children, className = '', glow = false, style = {}, ...props }) => (
  <div className={`relative overflow-hidden ${className}`} {...props}
    style={{
      background: glow ? 'rgba(18,14,4,0.85)' : 'rgba(14,10,3,0.8)',
      border: `1px solid rgba(234,179,8,${glow ? '0.35' : '0.18'})`,
      backdropFilter: 'blur(10px)',
      boxShadow: glow
        ? '0 0 25px rgba(234,179,8,0.08), inset 0 0 20px rgba(234,179,8,0.03)'
        : '0 0 10px rgba(234,179,8,0.03), inset 0 0 10px rgba(234,179,8,0.02)',
      ...style,
    }}>
    <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l z-10" style={{ borderColor: 'rgba(250,204,21,0.4)' }} />
    <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r z-10" style={{ borderColor: 'rgba(250,204,21,0.4)' }} />
    <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l z-10" style={{ borderColor: 'rgba(250,204,21,0.4)' }} />
    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r z-10" style={{ borderColor: 'rgba(250,204,21,0.4)' }} />
    <motion.div className="absolute inset-0 pointer-events-none"
      style={{ background: 'linear-gradient(135deg, transparent 40%, rgba(234,179,8,0.04) 50%, transparent 60%)', backgroundSize: '300% 300%' }}
      animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'linear' }} />
    {children}
  </div>
);

const ALLOCATIONS = [
  { label: 'Season Vault', pct: 35, tokens: '350M', color: '#ff6b00', icon: Trophy },
  { label: 'Liquidity Pool', pct: 45, tokens: '450M', color: '#10b981', icon: Droplets },
  { label: 'Airdrop', pct: 10, tokens: '100M', color: '#f59e0b', icon: Coins },
  { label: 'Team', pct: 10, tokens: '100M', color: '#ef4444', icon: Shield },
];


export default function StatsPage() {
  const navigate = useNavigate();
  const [tokenData, setTokenData] = useState(null);
  const [presaleData, setPresaleData] = useState(null);
  const [holderCount, setHolderCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const { isDemo } = useDemo();

  const fetchAllData = async () => {
    if (isDemo) {
      setTokenData({ price: 0.000042, priceChange24h: 12.5, marketCap: 420000, volume24h: 85000, liquidity: 210000, mintAddress: 'GFG...demo' });
      setPresaleData({ total_participants: 1847 });
      setHolderCount(892);
      setIsLive(true);
      setLastUpdated(new Date());
      setLoading(false);
      return;
    }
    try {
      const [marketRes, presaleRes, holderRes] = await Promise.all([
        fetch(`${API_URL}/api/market/cap`).catch(() => null),
        fetch(`${API_URL}/api/airdrop/info`).catch(() => null),
        fetch(`${API_URL}/api/token/holder-count`).catch(() => null),
      ]);
      if (marketRes?.ok) {
        const data = await marketRes.json();
        if (data && !data.is_mock && data.price_usd > 0) {
          setTokenData({
            price: data.price_usd || 0, priceChange24h: data.price_change_24h || 0,
            marketCap: data.market_cap || 0, volume24h: data.volume_24h || 0,
            liquidity: data.liquidity_usd || 0, mintAddress: data.mint_address || '',
          });
          setIsLive(true);
        }
      }
      if (presaleRes?.ok) setPresaleData(await presaleRes.json());
      if (holderRes?.ok) { const d = await holderRes.json(); setHolderCount(d.holder_count || 0); }
      setLastUpdated(new Date());
    } catch {}
    finally { setLoading(false); setIsRefreshing(false); }
  };

  useEffect(() => { fetchAllData(); const i = setInterval(fetchAllData, 30000); return () => clearInterval(i); }, []);

  return (
    <div className="min-h-screen relative text-white" data-testid="stats-page"
      style={{ background: '#0e0a02' }}>

      <style>{`
        @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
      `}</style>

      {/* Background */}
      <div className="fixed inset-0 z-0" style={{ background: 'radial-gradient(ellipse at 50% 20%, #1a1505 0%, #120e03 40%, #0e0a02 100%)' }} />
      <div className="fixed inset-0 z-[1] pointer-events-none">
        <motion.div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 50% 25%, rgba(234,179,8,0.07) 0%, transparent 50%)' }}
          animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 4, repeat: Infinity }} />
      </div>

      {/* CRT */}
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay" style={{
        background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.18) 50%)', backgroundSize: '100% 4px', opacity: 0.1,
      }} />

      {/* Particles — gold embers */}
      <div className="fixed inset-0 z-[2] pointer-events-none overflow-hidden">
        {[...Array(10)].map((_, i) => (
          <motion.div key={`p-${i}`} className="absolute rounded-full"
            style={{
              left: `${5 + i * 10}%`, bottom: '-8px',
              width: `${3 + Math.random() * 2}px`, height: `${3 + Math.random() * 2}px`,
              background: i % 2 === 0 ? '#fbbf24' : '#eab308',
              boxShadow: `0 0 ${6 + Math.random() * 6}px rgba(234,179,8,0.7)`,
            }}
            animate={{ y: [0, -650], x: [0, (Math.random() - 0.5) * 30], opacity: [0, 0.7, 0.25, 0] }}
            transition={{ duration: 6 + Math.random() * 4, delay: i * 0.6, repeat: Infinity, ease: 'easeOut' }}
          />
        ))}
      </div>

      <BackButton to="/" />

      {/* ═══ GREMLIN CHARACTER SPLASH — AAA game menu style ═══ */}
      <div className="fixed right-0 top-0 bottom-0 z-[3] pointer-events-none hidden lg:block" style={{ width: '55%' }}>
        {/* The gremlin — with CSS mask to feather ALL edges */}
        <motion.img
          src="/images/characters/gremlin_hacker.png"
          alt=""
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, delay: 0.5, ease: 'easeOut' }}
          className="absolute right-[-5%] bottom-0 h-[105%] w-auto object-contain object-right-bottom"
          style={{
            filter: 'brightness(1.15) contrast(1.15) saturate(1.3)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 25%, black 85%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 10%, black 85%, transparent 100%)',
            WebkitMaskComposite: 'destination-in',
            maskImage: 'linear-gradient(to right, transparent 0%, black 25%, black 85%, transparent 100%), linear-gradient(to bottom, transparent 0%, black 10%, black 85%, transparent 100%)',
            maskComposite: 'intersect',
          }}
        />
        {/* Golden rim light glow */}
        <motion.div className="absolute" style={{
          right: '18%', top: '12%', width: '200px', height: '300px',
          background: 'radial-gradient(ellipse, rgba(234,179,8,0.08) 0%, transparent 70%)',
          filter: 'blur(30px)',
        }}
          animate={{ opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Red eye glow */}
        <motion.div className="absolute" style={{
          right: '25%', top: '18%', width: '60px', height: '60px',
          background: 'radial-gradient(circle, rgba(239,68,68,0.2) 0%, transparent 70%)',
          filter: 'blur(15px)',
        }}
          animate={{ opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* ═══ CENTERED CONTENT ═══ */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pt-20 pb-16">

        {/* ═══ HEADER ═══ */}
        <div className="mb-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="relative">
              <div className="absolute -inset-x-6 -inset-y-3 pointer-events-none" style={{
                background: 'radial-gradient(ellipse at 40% 60%, rgba(234,179,8,0.1) 0%, transparent 70%)', filter: 'blur(12px)',
              }} />
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] uppercase tracking-[0.5em] mb-1 relative"
                style={{ fontFamily: "'Cinzel', serif", color: 'rgba(250,204,21,0.65)' }}>
                {isLive ? 'Live from the Sewers' : 'Transmission Incoming'}
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
                className="relative text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider inline-block"
                style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }} data-testid="stats-title">
                <span className="absolute inset-0" style={{ color: 'transparent', WebkitTextStroke: '2px rgba(234,179,8,0.25)', filter: 'blur(6px)' }}>THE PULSE</span>
                <span className="relative" style={{
                  background: 'linear-gradient(180deg, #fef9c3 0%, #fde68a 15%, #fbbf24 30%, #eab308 50%, #ca8a04 70%, #a16207 90%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 25px rgba(234,179,8,0.5)) drop-shadow(0 0 50px rgba(234,179,8,0.2))',
                }}>THE PULSE</span>
                <motion.span className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(254,249,195,0.4) 50%, transparent 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundSize: '200% 100%' }}
                  animate={{ backgroundPosition: ['200% 0%', '-200% 0%'] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}>THE PULSE</motion.span>
              </motion.h1>
            </div>

            {/* Status + Refresh */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2"
                style={{ border: `1px solid ${isLive ? 'rgba(34,197,94,0.3)' : 'rgba(234,179,8,0.3)'}`, background: `${isLive ? 'rgba(34,197,94,0.06)' : 'rgba(234,179,8,0.06)'}` }}>
                <motion.div className="w-2 h-2 rounded-full" style={{ background: isLive ? '#22c55e' : '#eab308', boxShadow: `0 0 8px ${isLive ? '#22c55e' : '#eab308'}` }}
                  animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: isLive ? 2 : 1.5, repeat: Infinity }} />
                <span className="text-xs font-bold tracking-wider" style={{ fontFamily: "'Cinzel', serif", color: isLive ? '#22c55e' : '#eab308' }}>{isLive ? 'LIVE' : 'STANDBY'}</span>
                {lastUpdated && <span className="text-[10px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,204,21,0.35)' }}>{lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
              </div>
              <motion.button onClick={() => { setIsRefreshing(true); fetchAllData(); }} disabled={isRefreshing}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold tracking-wider transition-all"
                style={{ fontFamily: "'Cinzel', serif", border: '1px solid rgba(234,179,8,0.2)', background: 'rgba(234,179,8,0.05)', color: '#eab308' }}
                whileHover={{ borderColor: 'rgba(234,179,8,0.4)' }}
                data-testid="stats-refresh-btn">
                <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} /> Refresh
              </motion.button>
            </div>
          </div>

          <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-3 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #eab308, #fbbf24, #eab308, transparent)' }} />
        </div>

        {loading ? (
          <div className="text-center py-20">
            <motion.div animate={{ opacity: [0.3, 1, 0.3], scale: [0.9, 1.1, 0.9] }} transition={{ duration: 2, repeat: Infinity }}>
              <Activity className="w-10 h-10 mx-auto" style={{ color: '#eab308', filter: 'drop-shadow(0 0 15px rgba(234,179,8,0.5))' }} />
            </motion.div>
            <p className="text-sm mt-4" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,204,21,0.4)' }}>Intercepting sewer signals...</p>
          </div>
        ) : isLive ? (
          /* ═══════════════════════ LIVE STATE ═══════════════════════ */
          <>
            {/* Price Hero */}
            <Panel glow className="p-5 md:p-8 mb-4 md:mb-6 text-center" data-testid="stats-price-hero">
              <p className="text-[10px] tracking-[0.3em] uppercase mb-2 md:mb-3 font-bold relative z-10" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(250,204,21,0.6)' }}>Current Price</p>
              <p className="text-4xl md:text-6xl lg:text-7xl font-bold relative z-10" style={{
                fontFamily: "'Impact', 'Arial Black', sans-serif",
                background: 'linear-gradient(180deg, #fef9c3, #fbbf24, #ca8a04)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 20px rgba(234,179,8,0.4))',
              }}>{formatPrice(tokenData.price)}</p>
              <div className="inline-flex items-center gap-1.5 mt-3 px-4 py-1.5 relative z-10"
                style={{
                  background: tokenData.priceChange24h >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${tokenData.priceChange24h >= 0 ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}>
                {tokenData.priceChange24h >= 0 ? <TrendingUp className="w-3.5 h-3.5" style={{ color: '#22c55e' }} /> : <TrendingDown className="w-3.5 h-3.5" style={{ color: '#ef4444' }} />}
                <span className="text-sm font-bold" style={{ fontFamily: "'Impact', sans-serif", color: tokenData.priceChange24h >= 0 ? '#22c55e' : '#ef4444' }}>
                  {tokenData.priceChange24h >= 0 ? '+' : ''}{tokenData.priceChange24h.toFixed(2)}% (24h)
                </span>
              </div>
            </Panel>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4 md:mb-6" data-testid="stats-primary-grid">
              {[
                { label: 'Market Cap', value: formatCompact(tokenData.marketCap), icon: BarChart3, color: '#fbbf24' },
                { label: '24h Volume', value: formatCompact(tokenData.volume24h), icon: Activity, color: '#f59e0b' },
                { label: 'Liquidity', value: formatCompact(tokenData.liquidity), icon: Droplets, color: '#eab308' },
                { label: 'Holders', value: holderCount > 0 ? holderCount.toLocaleString() : '—', icon: Users, color: '#a855f7' },
              ].map(s => (
                <Panel key={s.label} className="p-3 md:p-4">
                  <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2 relative z-10">
                    <s.icon className="w-3 h-3 md:w-3.5 md:h-3.5" style={{ color: s.color, filter: `drop-shadow(0 0 3px ${s.color}60)` }} />
                    <span className="text-[8px] md:text-[9px] tracking-[0.1em] md:tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(250,204,21,0.5)' }}>{s.label}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold relative z-10" style={{
                    fontFamily: "'Impact', 'Arial Black', sans-serif", color: s.color,
                    filter: `drop-shadow(0 0 4px ${s.color}40)`,
                  }}>{s.value}</p>
                </Panel>
              ))}
            </div>

            {/* DexScreener Chart or Demo Chart */}
            <Panel className="mb-4 md:mb-6 overflow-hidden" data-testid="dexscreener-chart">
              <div className="flex items-center gap-2 px-4 md:px-5 pt-3 md:pt-4 pb-2 relative z-10">
                <BarChart3 className="w-4 h-4" style={{ color: '#fbbf24', filter: 'drop-shadow(0 0 3px rgba(234,179,8,0.4))' }} />
                <span className="text-xs tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#fbbf24' }}>Live Chart</span>
              </div>
              {isDemo ? (
                <div className="relative px-2 md:px-5 pb-3 md:pb-5 z-10 h-[280px] md:h-[400px]">
                  <iframe
                    src="https://dexscreener.com/solana/So11111111111111111111111111111111111111112?embed=1&theme=dark&trades=0&info=0"
                    title="Solana Chart" className="w-full h-full" style={{ border: 'none' }} loading="lazy" />
                </div>
              ) : (
                <iframe
                  src={`https://dexscreener.com/solana/${tokenData.mintAddress}?embed=1&theme=dark&trades=0&info=0`}
                  title="DexScreener Chart" className="w-full relative z-10 h-[280px] md:h-[400px]" style={{ border: 'none' }} loading="lazy" />
              )}
            </Panel>

            {/* CTAs */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <div data-testid="stats-buy-btn"
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold tracking-wider uppercase cursor-default"
                style={{ fontFamily: "'Cinzel', serif", background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.15)', color: 'rgba(234,179,8,0.35)' }}>
                Token Launch Coming Soon
              </div>
              <a href="https://discord.gg/3wH7NaVXxP" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold tracking-wider uppercase transition-all hover:scale-105"
                style={{ fontFamily: "'Cinzel', serif", background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(250,204,21,0.45)' }}>
                <Users className="w-4 h-4" /> The Sewer Syndicate <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </>
        ) : (
          /* ═══════════════════════ PRE-LAUNCH STATE ═══════════════════════ */
          <>
            {/* Price Placeholder */}
            <Panel glow className="p-8 mb-6 text-center" data-testid="prelaunch-price">
              <p className="text-[10px] tracking-[0.3em] uppercase mb-3 font-bold relative z-10" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(250,204,21,0.5)' }}>Current Price</p>
              <motion.p className="text-5xl lg:text-6xl font-bold relative z-10" style={{
                fontFamily: "'Impact', 'Arial Black', sans-serif", color: 'rgba(234,179,8,0.12)',
              }}>$0.000000</motion.p>
              <motion.div className="inline-flex items-center gap-2 mt-3 px-4 py-1.5 relative z-10"
                style={{ background: 'rgba(234,179,8,0.05)', border: '1px solid rgba(234,179,8,0.15)' }}
                animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 3, repeat: Infinity }}>
                <Eye className="w-3.5 h-3.5" style={{ color: 'rgba(250,204,21,0.4)' }} />
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,204,21,0.4)' }}>Price tracking activates at launch</span>
              </motion.div>
            </Panel>

            {/* Token Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4 md:mb-6" data-testid="prelaunch-stats">
              {[
                { label: 'Total Supply', value: '1B', sub: '$GFG', icon: PieChart, color: '#fbbf24' },
                { label: 'Holders', value: holderCount > 0 ? holderCount.toLocaleString() : '—', sub: 'wallets', icon: Users, color: '#a855f7' },
                { label: 'Airdrop', value: presaleData?.status === 'accumulating' ? 'Active' : 'Sealed', sub: presaleData?.status === 'accumulating' ? 'earning pts' : 'pending', icon: Coins, color: '#22c55e' },
                { label: 'Fair Launch', value: 'No Presale', sub: 'everyone equal', icon: Shield, color: '#3b82f6' },
              ].map(s => (
                <Panel key={s.label} className="p-4">
                  <div className="flex items-center gap-1.5 mb-2 relative z-10">
                    <s.icon className="w-3 h-3" style={{ color: `${s.color}`, filter: `drop-shadow(0 0 3px ${s.color}60)` }} />
                    <span className="text-[9px] tracking-[0.12em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(250,204,21,0.45)' }}>{s.label}</span>
                  </div>
                  <p className="text-xl font-bold relative z-10" style={{ fontFamily: "'Impact', sans-serif", color: s.color }}>{s.value}</p>
                  <p className="text-[9px] relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,204,21,0.3)' }}>{s.sub}</p>
                </Panel>
              ))}
            </div>

            {/* Allocation + Chart — 2 column */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-8">
              {/* Allocation — hidden on mobile */}
              <div className="hidden md:block md:col-span-5">
                <Panel className="p-5" data-testid="prelaunch-allocation">
                  <div className="flex items-center gap-2 mb-4 relative z-10">
                    <PieChart className="w-4 h-4" style={{ color: '#fbbf24', filter: 'drop-shadow(0 0 3px rgba(234,179,8,0.4))' }} />
                    <span className="text-xs tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#fbbf24' }}>Token Allocation</span>
                  </div>
                  {/* Bar */}
                  <div className="flex h-4 overflow-hidden mb-4 gap-px relative z-10" style={{ border: '1px solid rgba(234,179,8,0.1)' }}>
                    {ALLOCATIONS.map((a, i) => (
                      <motion.div key={a.label} initial={{ width: 0 }} animate={{ width: `${a.pct}%` }}
                        transition={{ duration: 0.8, delay: 0.3 + i * 0.1 }}
                        className="h-full" style={{ background: a.color, opacity: 0.8 }} />
                    ))}
                  </div>
                  {/* Legend */}
                  <div className="space-y-2 relative z-10">
                    {ALLOCATIONS.map(a => (
                      <div key={a.label} className="flex items-center gap-2">
                        <div className="w-2 h-2 flex-shrink-0" style={{ background: a.color }} />
                        <span className="text-xs flex-1" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,220,150,0.65)' }}>{a.label}</span>
                        <span className="text-xs font-bold" style={{ fontFamily: "'Impact', sans-serif", color: a.color }}>{a.pct}%</span>
                        <span className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,204,21,0.3)' }}>{a.tokens}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>

              {/* Chart placeholder */}
              <div className="md:col-span-7">
                <Panel className="p-5 h-full flex flex-col" data-testid="prelaunch-chart-placeholder">
                  <div className="flex items-center gap-2 mb-4 relative z-10">
                    <BarChart3 className="w-4 h-4" style={{ color: '#fbbf24', filter: 'drop-shadow(0 0 3px rgba(234,179,8,0.4))' }} />
                    <span className="text-xs tracking-[0.15em] uppercase font-bold" style={{ fontFamily: "'Cinzel', serif", color: '#fbbf24' }}>Live Chart</span>
                  </div>
                  <div className="flex-1 relative overflow-hidden z-10" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(234,179,8,0.08)', minHeight: '180px' }}>
                    <svg className="w-full h-full opacity-15" viewBox="0 0 400 160" preserveAspectRatio="none">
                      <path d="M0,120 Q50,100 100,110 T200,75 T300,90 T400,50" fill="none" stroke="#eab308" strokeWidth="2" />
                      <path d="M0,130 Q50,115 100,120 T200,95 T300,100 T400,65" fill="none" stroke="#eab308" strokeWidth="1" opacity="0.3" />
                    </svg>
                    <motion.div className="absolute inset-0 flex items-center justify-center"
                      animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 3, repeat: Infinity }}>
                      <span className="text-xs tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,204,21,0.3)' }}>Chart activates at launch</span>
                    </motion.div>
                  </div>
                  <p className="text-[9px] mt-3 relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,204,21,0.25)' }}>
                    Powered by DexScreener — real-time data appears when $GFG hits the DEX
                  </p>
                </Panel>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <button onClick={() => navigate('/airdrop')}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold tracking-wider uppercase transition-all hover:scale-105"
                style={{ fontFamily: "'Cinzel', serif", background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.35)', color: '#fbbf24' }}>
                <Coins className="w-4 h-4" /> Join Airdrop
              </button>
              <a href="https://discord.gg/3wH7NaVXxP" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold tracking-wider uppercase transition-all hover:scale-105"
                style={{ fontFamily: "'Cinzel', serif", background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(250,204,21,0.45)' }}>
                <Users className="w-4 h-4" /> The Sewer Syndicate <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-[9px] tracking-[0.2em] uppercase font-bold" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(250,204,21,0.2)' }}>
            Data powered by DexScreener. Always DYOR. The gremlins are not financial advisors.
          </p>
        </div>
      </div>
    </div>
  );
}


// Fake animated chart for demo mode
function DemoChart() {
  const points = [
    18, 22, 20, 28, 25, 35, 32, 42, 38, 48, 44, 55, 50, 58, 52, 62, 58, 68, 65, 72,
    68, 75, 70, 80, 76, 82, 78, 85, 80, 88, 84, 90, 86, 92, 88, 95, 90, 94, 88, 92,
    86, 90, 84, 88, 82, 86, 80, 85, 82, 88, 85, 92, 88, 95, 92, 98, 95, 100, 96, 98,
  ];
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const h = 300;
  const w = 100;

  const pathD = points.map((p, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - ((p - min) / range) * (h * 0.8) - h * 0.1;
    return `${i === 0 ? 'M' : 'L'}${x},${y}`;
  }).join(' ');
  const areaD = pathD + ` L${w},${h} L0,${h} Z`;

  const labels = ['4h ago', '3h ago', '2h ago', '1h ago', 'Now'];

  return (
    <div className="w-full h-full flex flex-col">
      {/* Price labels */}
      <div className="flex justify-between mb-1">
        <span className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(234,179,8,0.3)' }}>$0.000042</span>
        <span className="text-[10px] font-bold" style={{ fontFamily: "'Courier New', monospace", color: '#22c55e' }}>+12.5%</span>
      </div>

      <div className="flex-1 relative">
        {/* Grid lines */}
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="absolute left-0 right-0 h-[1px]" style={{ top: `${25 * i}%`, background: 'rgba(234,179,8,0.04)' }} />
        ))}

        <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-full">
          <defs>
            <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22c55e" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="50%" stopColor="#22c55e" />
              <stop offset="100%" stopColor="#22c55e" />
            </linearGradient>
          </defs>
          <motion.path d={areaD} fill="url(#chartGrad)"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} />
          <motion.path d={pathD} fill="none" stroke="url(#lineGrad)" strokeWidth="0.5"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2, ease: 'easeOut' }} />
          {/* Current price dot */}
          <motion.circle cx={w} cy={h - ((points[points.length - 1] - min) / range) * (h * 0.8) - h * 0.1}
            r="1.5" fill="#22c55e"
            animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>
          </motion.circle>
        </svg>
      </div>

      {/* Time labels */}
      <div className="flex justify-between mt-1">
        {labels.map(l => (
          <span key={l} className="text-[8px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(234,179,8,0.2)' }}>{l}</span>
        ))}
      </div>
    </div>
  );
}
